import {EventEmitter, Injectable} from '@angular/core';
import {TradingInstrument} from "../trading-instruments/trading-instrument.class";
import {TradingInstrumentsService} from "../trading-instruments/trading-instruments-service.interface";
import {DetectMethodChanges, isVoid} from "../utils";
import {
    AddQuoteStream, ChangeHistoryDelta,
    DeleteSavedScript,
    GenerateNextQuote,
    GetLoadedStreams, GetQgHistoricalDatasetRange, GetQgHistoricalDatasetRangeReply,
    GetSavedScript,
    GetSavedScriptReply,
    GetSingleQuoteShell,
    GetSingleQuoteShellReply,
    PauseQuoteGeneratorStream,
    PlayQuoteGeneratorConfig,
    PlayQuoteGeneratorStream,
    RemoveQuoteGeneratorStream,
    ResetStreamPriceToMarket,
    SaveCustomScript,
    SetCustomScript,
    SetQuoteGeneratorMode, SetQuoteGeneratorModeAndSource,
    SetQuoteGeneratorStreamFrequency,
    SetQuoteStreamJumpStep, SetQuoteStreamJumpToDate,
    SetSavedScript,
    SetTrendBias
} from "../shell-communication/shell-operations-protocol";
import {ShellClientService} from "../shell-communication/shell-client.service";
import {
    QuoteGeneratorLoadedStreamDto,
    QuoteGeneratorProgressDto,
    QuoteGeneratorQuoteDto
} from "../shell-communication/shell-dto-protocol";
import {ProtocolCommand} from "../service-model/protocol-command.interface";
import {QuoteGeneratorMode, QuoteGeneratorQuoteSource, QuoteStream} from "./quote.stream";
import {AtmStrikeService} from "../common-services/atm-strike-service/atm-strike.service";
import {SessionService} from "../authentication/session-service.service";
import {MessageBusService} from "../message-bus.service";
import {LastQuoteCacheService} from "../last-quote-cache.service";

@Injectable({providedIn: 'root'})
export class QuoteGeneratorService {

    constructor(
        private readonly _tiService: TradingInstrumentsService,
        private readonly _shellService: ShellClientService,
        private readonly _atmService: AtmStrikeService,
        private readonly _sessionService: SessionService,
        private readonly _messageBus: MessageBusService,
        private readonly _lastQuoteCache: LastQuoteCacheService
    ) {
    }


    private _atmIndex: Record<string, number> = {};

    private _registeredStreams: QuoteStream[] = [];

    private _atmPoller: number;

    atmChanged$ = new EventEmitter<{ ticker: string, atm: number }>();

    init() {

        clearInterval(this._atmPoller);
        this._atmIndex = {};

        if (!this._sessionService.loginResult.isQuoteGeneratorShell) {
            return;
        }

        if (this.atmChanged$) {
            this.atmChanged$.complete();
        }

        this.atmChanged$ = new EventEmitter<{ ticker: string, atm: number }>();

        this._atmPoller = setInterval(async () => {
            await this.fetchAtm("SPX");
            await this.fetchAtm("SPY");
            await this.fetchAtm(this._tiService.currentEsContract);
        }, 2000) as any;


        this._messageBus.of<any>('Qg.CursorDateRequest')
            .subscribe((request) => this.onCursorDateRequest(request.payload));
    }

    async getRegisteredStreams(): Promise<QuoteStream[]> {

        const qry = new GetLoadedStreams();

        const streams = await this._shellService
            .processQuery<QuoteGeneratorLoadedStreamDto[]>(qry);

        const quoteStreams = streams.map(x => QuoteStream.fromDto(x, this._tiService));

        this._registeredStreams = quoteStreams;

        return this._registeredStreams;
    }

    async addStream(tradingInstrument: TradingInstrument): Promise<void> {
        const number = this._registeredStreams.findIndex(x => x.ticker === tradingInstrument.ticker);

        if (number !== -1) {
            return;
        }

        const stream = new QuoteStream(tradingInstrument);

        this._registeredStreams.push(stream);

        const cmd = new AddQuoteStream(tradingInstrument.ticker);

        await this._shellService.processCommand(cmd);
    }

    removeStream(stream: QuoteStream) {
        const number = this._registeredStreams.indexOf(stream);

        if (number === -1) {
            return;
        }

        this._registeredStreams.splice(number, 1);

        const cmd = new RemoveQuoteGeneratorStream(stream.ticker);
        this._shellService.processCommand(cmd)
            .then(() => {
            })
            .catch(() => console.log('Failed to remove stream'));
    }

    async nextQuote(ticker: string, direction: 'Backward' | 'Last' | 'Forward', count: number, step?: number): Promise<void> {
        const cmd = new GenerateNextQuote(ticker, direction, count, step);
        await this._shellService.processCommand(cmd);
    }

    onQuoteGeneratorQuote(payload: QuoteGeneratorQuoteDto) {
        const stream = this._registeredStreams.find(x => x.ticker === payload.ticker);
        if (isVoid(stream)) {
            return;
        }
        stream.lastPrice = payload.lastPx;
        if (stream.mode === 'History') {
            stream.lastTimestamp = payload.isoTimestamp;
            this._messageBus.publish({
                topic: 'Qg.CursorDateChangedMessage',
                payload: {
                    ticker: stream.ticker,
                    cursorDate: stream.lastTimestamp
                }
            });
        }

        if (payload.ticker.startsWith("@")) {
            return;
        }

        this._messageBus.publish({topic: 'Qg.NextQuoteGenerated', payload: {quote: payload}});
    }

    async setMode(ticker: string, mode: QuoteGeneratorMode) {
        const cmd = new SetQuoteGeneratorMode(ticker, mode);
        await this._shellService.processCommand(cmd);
        this._messageBus.publishAsync({
            topic: 'Qg.StreamModeChanged',
            payload: {
                stream: ticker,
                mode: mode
            }
        });
    }

    async toggleStreamPlaying(stream: QuoteStream) {
        let cmd: ProtocolCommand;
        if (stream.isRunning) {
            cmd = new PauseQuoteGeneratorStream(stream.ticker);
        } else {
            const cfg: PlayQuoteGeneratorConfig = {
                ticker: stream.ticker,
                frequency: stream.frequency,
                customScript: stream.customScript,
                quoteStep: stream.quoteStep
            };
            cmd = new PlayQuoteGeneratorStream(cfg);
        }
        await this._shellService.processCommand(cmd);
    }

    async setStreamFrequency(stream: QuoteStream) {
        const cmd = new SetQuoteGeneratorStreamFrequency(
            stream.ticker,
            stream.frequency
        );

        await this._shellService.processCommand(cmd);
    }

    onQuoteGeneratorProgress(payload: QuoteGeneratorProgressDto) {
        const stream = this._registeredStreams.find(x => x.ticker === payload.ticker);
        if (isVoid(stream)) {
            return;
        }
        stream.progress = payload.progress;

        if (stream.progress >= 1) {
            if (stream.isRunning) {
                this.toggleStreamPlaying(stream).then(() => {
                });
            }
        }
    }

    onQuoteGeneratorStreamLoaded(payload: QuoteGeneratorLoadedStreamDto) {
        const number = this._registeredStreams.findIndex(x => payload.ticker === x.ticker);
        if (number > -1) {
            return;
        }
        const stream = QuoteStream.fromDto(payload, this._tiService);
        this._registeredStreams.push(stream);
    }

    async setCustomScript(stream: QuoteStream, script: string): Promise<void> {
        const cmd = new SetCustomScript(stream.ticker, script);
        await this._shellService.processCommand(cmd);
    }

    async setSavedScript(stream: QuoteStream, script: string) {
        const cmd = new SetSavedScript(stream.ticker, script);
        await this._shellService.processCommand(cmd);
    }

    async resetStreamPriceToMarket(ticker: string) {
        const cmd = new ResetStreamPriceToMarket(ticker);
        await this._shellService.processCommand(cmd);
    }

    async setTrendBias(msg: QuoteStream) {
        const cmd = new SetTrendBias(msg.ticker, msg.trendBias);
        await this._shellService.processCommand(cmd);
    }

    async saveCustomScript(name: string, description: string, script: string, isEditMode: boolean) {
        const cmd = new SaveCustomScript(name, description, script, isEditMode);
        await this._shellService.processCommand(cmd);
    }

    async getSavedScript(id: string) {
        const qry = new GetSavedScript(id);
        const reply = await this._shellService.processQuery<GetSavedScriptReply>(qry);
        return reply.script;
    }

    async deleteScript(payload: any) {
        const cmd = new DeleteSavedScript(payload.script.id);
        await this._shellService.processCommand(cmd);
    }

    private async fetchAtm(ticker: string) {
        const qry = new GetSingleQuoteShell(ticker, true);

        const result = await this._shellService.processQuery<GetSingleQuoteShellReply>(qry);

        this._messageBus.publishAsync({
            topic: 'LiveQuoteReceived',
            payload: {
                ticker,
                lastPx: result.lastPx,
                change: result.change
            }
        });

        const currentAtm = this._atmService.calculateCurrentAtm(ticker, result.lastPx);

        const prevAtm = this._atmIndex[ticker];


        if (prevAtm === currentAtm) {
            return;
        }

        this._atmIndex[ticker] = currentAtm;

        this.atmChanged$.emit({ticker, atm: currentAtm});
    }

    getCurrentAtm(ticker: string): number | undefined {
        return this._atmIndex[ticker];
    }

    async setModeAndSource(ticker: string, mode: QuoteGeneratorMode, quoteSource: QuoteGeneratorQuoteSource) {
        this._lastQuoteCache.clearQuotesCache();
        const cmd = new SetQuoteGeneratorModeAndSource(ticker, mode, quoteSource);
        await this._shellService.processCommand(cmd);
        this._messageBus.publishAsync({
            topic: 'Qg.StreamModeChanged',
            payload: {
                stream: ticker,
                mode
            }
        });
    }

    dispose() {
        clearInterval(this._atmPoller);
    }

    getStream(ticker: string): QuoteStream {
        const quoteStream = this._registeredStreams.find(x => x.ticker === ticker);
        return quoteStream;
    }

    isTheoreticalPrices(ticker: string) {

        if (isVoid(ticker)) {
            return false;
        }

        if (!this._sessionService.loginResult.isQuoteGeneratorShell) {
            return false;
        }

        const stream = this.getStream(ticker);

        if (isVoid(stream)) {
            return false;
        }

        return stream.quoteSource === 'Theoretical';
    }

    async setJumpToDate(stream: QuoteStream) {
        const cmd = new SetQuoteStreamJumpToDate(stream.ticker, stream.jumpToDate);
        await this._shellService.processCommand(cmd);

        this._messageBus.publish({
            topic: 'Qg.JumpToDateChangedMessage',
            payload: {
                ticker: stream.ticker,
                jumpToDate: stream.jumpToDate,
            }
        });
    }

    async setJumpStep(stream: QuoteStream) {
        const cmd = new SetQuoteStreamJumpStep(stream.ticker, stream.jumpStep);
        await this._shellService.processCommand(cmd);
    }

    async requestHistoricalDatasetRange(stream: QuoteStream) {
        const qry = new GetQgHistoricalDatasetRange(stream.ticker);
        const range = await this._shellService.processQuery<GetQgHistoricalDatasetRangeReply>(qry);

        if (isVoid(range.startPoint)) {
            stream.dataset = null;
            return;
        }

        stream.dataset = {
            startPoint: range.startPoint,
            endPoint: range.endPoint,
            nonTradingDates: range.nonTradingDates.map(x => new Date(x))
        } as any;
    }

    private onCursorDateRequest(payload: { ticker: string, cursorDate: string }) {
        const quoteStream = this._registeredStreams.find(x => x.ticker === payload.ticker);
        payload.cursorDate = quoteStream?.mode === 'History' ? quoteStream.lastTimestamp : undefined;
    }

    async changeHistoryDelta(ticker: string, historyDelta: number) {
        const cmd = new ChangeHistoryDelta(
            ticker,
            historyDelta|| 0
        );
        await this._shellService.processCommand(cmd);
    }
}
